import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import {
  getDataAPI,
  postDataAPI,
  deleteDataAPI,
} from "../../../services/apiWrapper";
import { ALERT } from "../../../store/actions/actionTypes";
import { readSheet } from "../../../utils/readSheet";
import { exportToCSV } from "../../../utils/exportCsv";
import CourseCascade from "./CourseCascade";

/**
 * LMS - Question Bank (Council/Admin + Faculty).
 *
 * Pick a course + subject, then add questions one by one or bulk-upload a sheet.
 * The server reuses the existing question_list / question_mcq tables, so a course
 * can hold thousands of questions for the online exam to draw a random mix from.
 */
export default function QuestionBank() {
  const dispatch = useDispatch();
  const token = () => localStorage.getItem("adminToken");
  const alert = (payload) => dispatch({ type: ALERT, payload });

  const [subjects, setSubjects] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [questions, setQuestions] = useState([]);
  const [count, setCount] = useState(null);

  const blank = { id: null, question: "", options: ["", "", "", ""], correct: 1 };
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(blank);

  const onCourse = async (cid) => {
    setCourseId(cid);
    setSubjectId("");
    setSubjects([]);
    setQuestions([]);
    setCount(null);
    if (!cid) return;
    try {
      const [subs, cnt] = await Promise.all([
        dispatch(getDataAPI(`admin/lms/content/subjects/${cid}`, token())),
        dispatch(getDataAPI(`admin/lms/questions/count?course_id=${cid}`, token())),
      ]);
      setSubjects(subs.data.data || []);
      setCount(cnt.data.data || []);
    } catch (err) {
      alert({ error: "Could not load subjects." });
    }
  };

  const loadQuestions = async (cid, sid) => {
    if (!cid || !sid) return;
    try {
      const res = await dispatch(getDataAPI(`admin/lms/questions?course_id=${cid}&subject_id=${sid}`, token()));
      setQuestions(res.data.data || []);
    } catch (e) {
      alert({ error: "Could not load questions." });
    }
  };

  const onSubject = (e) => {
    setSubjectId(e.target.value);
    loadQuestions(courseId, e.target.value);
  };

  /* ---- single add/edit ---- */
  const openAdd = () => {
    setForm(blank);
    setShow(true);
  };
  const openEdit = (q) => {
    const opts = (q.options || []).map((o) => o.answer);
    while (opts.length < 2) opts.push("");
    const correct = (q.options || []).findIndex((o) => Number(o.is_correct) === 1) + 1;
    setForm({ id: q.id, question: q.question, options: opts, correct: correct > 0 ? correct : 1 });
    setShow(true);
  };
  const setOpt = (i, v) => {
    const o = [...form.options];
    o[i] = v;
    setForm({ ...form, options: o });
  };
  const save = async (e) => {
    e.preventDefault();
    const options = form.options.map((o) => o.trim()).filter((o) => o !== "");
    if (options.length < 2) {
      alert({ error: "Add at least two options." });
      return;
    }
    if (form.correct < 1 || form.correct > options.length) {
      alert({ error: "Pick which option is correct." });
      return;
    }
    try {
      await dispatch(
        postDataAPI(
          "admin/lms/question",
          { id: form.id, course_id: courseId, subject_id: subjectId, question: form.question, options, correct: form.correct },
          token()
        )
      );
      alert({ success: "Question saved." });
      setShow(false);
      loadQuestions(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not save question." });
    }
  };
  const remove = async (q) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await dispatch(deleteDataAPI(`admin/lms/question/${q.id}`, token()));
      alert({ success: "Question removed." });
      loadQuestions(courseId, subjectId);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not delete." });
    }
  };

  /* ---- bulk ---- */
  const downloadTemplate = () => {
    exportToCSV(
      [
        { question: "What is 2 + 2?", option_1: "3", option_2: "4", option_3: "5", option_4: "6", correct_option: 2 },
        { question: "Capital of India?", option_1: "Delhi", option_2: "Mumbai", option_3: "", option_4: "", correct_option: 1 },
      ],
      "lms-questions-template"
    );
  };
  const onBulk = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!courseId || !subjectId) {
      alert({ error: "Choose a course and subject first." });
      return;
    }
    try {
      const { rows } = await readSheet(file);
      if (!rows || rows.length === 0) {
        alert({ error: "The file had no rows." });
        return;
      }
      const res = await dispatch(
        postDataAPI("admin/lms/questions/bulk", { course_id: courseId, subject_id: subjectId, rows }, token())
      );
      const d = res.data.data;
      alert({ success: res.data.message });
      if (d.skipped && d.skipped.length) {
        // surface a short reason summary
        const first = d.skipped.slice(0, 3).map((s) => `row ${s.row}: ${s.reason}`).join(" | ");
        alert({ error: `Skipped ${d.skipped.length}. ${first}` });
      }
      loadQuestions(courseId, subjectId);
      const cnt = await dispatch(getDataAPI(`admin/lms/questions/count?course_id=${courseId}`, token()));
      setCount(cnt.data.data || []);
    } catch (err) {
      alert({ error: err.response?.data?.message || "Could not read / upload the file." });
    }
  };

  const subjCount = count && subjectId ? count.find((c) => String(c.id) === String(subjectId)) : null;

  return (
    <div className="container-fluid" style={{ padding: 20 }}>
      <h4 className="mb-3">Question Bank</h4>

      <CourseCascade onCourse={(id) => onCourse(id)} />

      <div className="row mb-3">
        <div className="col-md-5">
          <label>Subject</label>
          <select className="form-control" value={subjectId} onChange={onSubject} disabled={!courseId}>
            <option value="">— select subject —</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.code ? `(${s.code})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {subjectId ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <b>{subjCount ? subjCount.questions : questions.length}</b> questions in this subject
            </div>
            <div>
              <Button size="sm" variant="outline-secondary" className="mr-2" onClick={downloadTemplate}>
                Download template
              </Button>{" "}
              <label className="btn btn-sm btn-outline-primary mr-2" style={{ marginBottom: 0 }}>
                Bulk upload
                <input type="file" accept=".csv,.xlsx,.xls" hidden onChange={onBulk} />
              </label>{" "}
              <Button size="sm" variant="primary" onClick={openAdd}>
                + Add Question
              </Button>
            </div>
          </div>

          <div className="alert alert-light" style={{ border: "1px solid #eee" }}>
            <small>
              <b>Sheet format:</b> columns <code>question</code>, <code>option_1</code>,
              <code> option_2</code>, <code>option_3</code>, <code>option_4</code>,
              <code> correct_option</code> (the correct option's number, 1–4). At least two
              options per row.
            </small>
          </div>

          <table className="table table-bordered bg-white">
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Question</th>
                <th>Options (✓ = correct)</th>
                <th style={{ width: 150 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {questions.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">No questions yet.</td>
                </tr>
              ) : (
                questions.map((q, i) => (
                  <tr key={q.id}>
                    <td>{i + 1}</td>
                    <td>{q.question}</td>
                    <td>
                      {(q.options || []).map((o) => (
                        <div key={o.id}>
                          {Number(o.is_correct) === 1 ? "✓ " : "• "}
                          {o.answer}
                        </div>
                      ))}
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" className="mr-2" onClick={() => openEdit(q)}>
                        Edit
                      </Button>{" "}
                      <Button size="sm" variant="outline-danger" onClick={() => remove(q)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </>
      ) : (
        <p className="text-muted">Choose a course and subject to manage its questions.</p>
      )}

      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <form onSubmit={save}>
          <Modal.Header closeButton>
            <Modal.Title>{form.id ? "Edit Question" : "Add Question"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group">
              <label>Question</label>
              <textarea
                className="form-control"
                rows={2}
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                required
              />
            </div>
            {form.options.map((o, i) => (
              <div className="form-group" key={i}>
                <label>
                  <input
                    type="radio"
                    name="correct"
                    checked={Number(form.correct) === i + 1}
                    onChange={() => setForm({ ...form, correct: i + 1 })}
                    style={{ marginRight: 6 }}
                  />
                  Option {i + 1} {Number(form.correct) === i + 1 ? "(correct)" : ""}
                </label>
                <input className="form-control" value={o} onChange={(e) => setOpt(i, e.target.value)} />
              </div>
            ))}
            <small className="text-muted">Select the radio next to the correct option. Leave unused options blank.</small>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShow(false)}>Cancel</Button>
            <Button variant="primary" type="submit">Save</Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}
